/**
 * useS3Dataset — generic FinTech Ops S3 fetch hook.
 *
 * Responsibilities:
 *   - Call getS3PayloadAndLastUpdatedAtData(prefix, lastUpdatedPrefix, category)
 *   - Apply a caller-supplied parse(raw, meta) to produce typed data
 *   - In-memory cache keyed by prefix, with TTL
 *   - Dedupe: concurrent callers for the same prefix share one inflight promise
 *   - AbortController tied to unmount
 *
 * Behaviour-preserving: same underlying S3 calls as legacy FintechContextProvider.
 * See Phase 4 of k-analysis-ops/04-refactoring-plan.md.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from 'src/logger';
import { getS3PayloadAndLastUpdatedAtData } from 'src/utilities/CommonUtilities';
import { FinTechOpsCategories } from '../../FinTechOpsConstants';

export interface MetadataLike {
  last_updated_at: string;
}

export interface UseS3DatasetOptions<Raw, Parsed> {
  prefix: string;
  lastUpdatedPrefix: string;
  category: FinTechOpsCategories;
  parse: (raw: Raw, meta: MetadataLike) => Parsed;
  /**
   * Cache TTL in ms. Default 5 minutes. 0 disables caching.
   * NOTE: cache is keyed by `prefix` only, so the first consumer's TTL wins. Consumers
   * mounting with a shorter TTL against the same prefix may see a longer-lived cache entry
   * from a prior mount until it expires naturally.
   */
  cacheTtlMs?: number;
}

export interface UseS3DatasetResult<Parsed> {
  data: Parsed | undefined;
  loading: boolean;
  error: Error | null;
  lastUpdatedAt: string | null;
  refetch: () => void;
}

interface CacheEntry<Parsed> {
  parsed: Parsed;
  lastUpdatedAt: string | null;
  expiresAt: number;
}

const DEFAULT_TTL_MS = 5 * 60 * 1000;

// Module-level caches, keyed by S3 prefix.
const cache = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, Promise<{ parsed: unknown; lastUpdatedAt: string | null }>>();

/** Test-only: reset the module-level cache + inflight maps. */
export const __resetS3DatasetCache = () => {
  cache.clear();
  inflight.clear();
};

export function useS3Dataset<Raw, Parsed>(opts: UseS3DatasetOptions<Raw, Parsed>): UseS3DatasetResult<Parsed> {
  const { prefix, lastUpdatedPrefix, category, parse, cacheTtlMs = DEFAULT_TTL_MS } = opts;

  const [data, setData] = useState<Parsed | undefined>(() => {
    const hit = cache.get(prefix) as CacheEntry<Parsed> | undefined;
    return hit && hit.expiresAt > Date.now() ? hit.parsed : undefined;
  });
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(() => {
    const hit = cache.get(prefix) as CacheEntry<Parsed> | undefined;
    return hit && hit.expiresAt > Date.now() ? hit.lastUpdatedAt : null;
  });
  const [loading, setLoading] = useState<boolean>(() => !cache.get(prefix));
  const [error, setError] = useState<Error | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  // Latest parse ref so cache stays stable across renders with new closures.
  const parseRef = useRef(parse);
  parseRef.current = parse;

  useEffect(() => {
    const abort = new AbortController();
    let cancelled = false;

    const run = async () => {
      // Cache hit — serve immediately without network.
      const hit = cache.get(prefix) as CacheEntry<Parsed> | undefined;
      if (hit && hit.expiresAt > Date.now() && refetchTick === 0) {
        setData(hit.parsed);
        setLastUpdatedAt(hit.lastUpdatedAt);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Dedup concurrent callers.
        let pending = inflight.get(prefix);
        if (!pending || refetchTick > 0) {
          pending = (async () => {
            const result = await getS3PayloadAndLastUpdatedAtData(prefix, lastUpdatedPrefix, category);
            // getS3PayloadAndLastUpdatedAtData swallows errors and returns undefined.
            if (!result) {
              throw new Error(`S3 fetch failed for ${category} (${prefix})`);
            }
            const [rawPayload, meta] = result as [Raw, MetadataLike];
            const parsed = parseRef.current(rawPayload, meta ?? { last_updated_at: '' });
            return { parsed, lastUpdatedAt: meta?.last_updated_at ?? null };
          })();
          inflight.set(prefix, pending);
          // Detach a cleanup + swallow handler so the inflight promise never surfaces as unhandled.
          // Consumers still see errors via their own `await pending` below.
          pending
            .finally(() => {
              if (inflight.get(prefix) === pending) inflight.delete(prefix);
            })
            .catch(() => {});
        }

        const { parsed, lastUpdatedAt: lua } = (await pending) as {
          parsed: Parsed;
          lastUpdatedAt: string | null;
        };

        if (cancelled || abort.signal.aborted) return;

        if (cacheTtlMs > 0) {
          cache.set(prefix, {
            parsed,
            lastUpdatedAt: lua,
            expiresAt: Date.now() + cacheTtlMs
          });
        }
        setData(parsed);
        setLastUpdatedAt(lua);
        setLoading(false);
      } catch (err) {
        if (cancelled || abort.signal.aborted) return;
        const e = err instanceof Error ? err : new Error(String(err));
        logger.error(`useS3Dataset(${category}) fetch failed`, e);
        setError(e);
        setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [prefix, lastUpdatedPrefix, category, cacheTtlMs, refetchTick]);

  const refetch = useCallback(() => {
    cache.delete(prefix);
    inflight.delete(prefix);
    setRefetchTick((t) => t + 1);
  }, [prefix]);

  return { data, loading, error, lastUpdatedAt, refetch };
}
